import { useCallback, useRef, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	Tooltip,
	Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import GetAppIcon from "@mui/icons-material/GetApp";
import DeleteIcon from "@mui/icons-material/Delete";
import { MESApiService, ErrorModal } from "@dexteel/mesf-core";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { BarrelAttachment } from "../models/Barrel";
import {
	useBarrelAttachments,
	useBarrelAttachmentMutations,
} from "../hooks/useBarrelAttachments";

interface BarrelAttachmentsProps {
	barrelId: number | null;
	disabled?: boolean;
}

export const BarrelAttachments = ({
	barrelId,
	disabled = false,
}: BarrelAttachmentsProps) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [attachmentToDelete, setAttachmentToDelete] =
		useState<BarrelAttachment | null>(null);

	const api = new MESApiService();

	// Query for attachments
	const { data: attachments = [], isLoading } = useBarrelAttachments({
		barrelId,
		enabled: barrelId != null,
	});

	// Mutations
	const { uploadAttachmentAsync, isUploading, deleteAttachment, isDeleting } =
		useBarrelAttachmentMutations({
			onUploadSuccess: () => {
				setNotification("File uploaded successfully");
			},
			onDeleteSuccess: () => {
				setNotification("File removed successfully");
				setDeleteModalOpen(false);
				setAttachmentToDelete(null);
			},
			onError: (err) => setError(err.message),
		});

	// Handle file upload
	const handleFileUpload = useCallback(
		async (files: File[]) => {
			if (!barrelId) {
				setError("Barrel must be saved before adding files");
				return;
			}

			setUploadingFiles(files);
			setError("");

			try {
				for (const file of files) {
					// 1. Upload file using MESApiService
					const uploadResponse = await api.uploadFiles([file], "barrels");

					if (uploadResponse?.result?.[0]) {
						const uploadFileId = uploadResponse.result[0].UploadFileId;
						const attachmentURL = uploadResponse.result[0].URL;

						// 2. Create attachment record in DB
						await uploadAttachmentAsync({
							barrelId,
							uploadFileId,
							attachmentName: file.name,
							attachmentURL,
						});
					}
				}
			} catch (err) {
				setError(`Failed to upload file: ${err}`);
			} finally {
				setUploadingFiles([]);
			}
		},
		[barrelId, api, uploadAttachmentAsync],
	);

	// Handle file selection
	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			handleFileUpload(Array.from(files));
		}
		// Reset input
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	// Handle delete
	const handleDeleteClick = (attachment: BarrelAttachment) => {
		setAttachmentToDelete(attachment);
		setDeleteModalOpen(true);
	};

	const handleConfirmDelete = () => {
		if (attachmentToDelete) {
			deleteAttachment(attachmentToDelete.BarrelAttachmentId);
		}
	};

	const isProcessing =
		isLoading || isUploading || isDeleting || uploadingFiles.length > 0;

	if (!barrelId) {
		return (
			<Typography variant="body2" color="text.secondary">
				Create the barrel first to upload files
			</Typography>
		);
	}

	return (
		<Box>
			{/* Hidden file input */}
			<input
				type="file"
				ref={fileInputRef}
				style={{ display: "none" }}
				accept=".dwg,.dxf,.pdf,.png,.jpg,.jpeg,.gif,.bmp"
				onChange={handleFileSelect}
				disabled={disabled || isProcessing}
			/>

			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Button
					variant="outlined"
					size="small"
					startIcon={
						isUploading || uploadingFiles.length > 0 ? (
							<CircularProgress size={16} />
						) : (
							<CloudUploadIcon />
						)
					}
					onClick={() => fileInputRef.current?.click()}
					disabled={disabled || isProcessing}
				>
					{isUploading || uploadingFiles.length > 0
						? "Uploading..."
						: "Upload File"}
				</Button>
				<Typography variant="caption" color="text.secondary">
					.dwg, .dxf, .pdf, images
				</Typography>
				{isLoading ? (
					<CircularProgress size={16} />
				) : (
					attachments.length === 0 && (
						<Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
							No files attached
						</Typography>
					)
				)}
			</Box>

			{attachments.length > 0 && (
				<List dense sx={{ py: 0, mt: 1, width: "100%" }}>
					{attachments.map((attachment) => (
						<ListItem
							key={attachment.BarrelAttachmentId}
							sx={{
								px: 0,
								py: 0.25,
								mb: 0.5,
								backgroundColor: "rgba(0, 0, 0, 0.02)",
								borderRadius: 1,
								"&:hover": {
									backgroundColor: "rgba(0, 0, 0, 0.04)",
								},
								pr: 10,
							}}
							disablePadding
						>
							<Box
								component="a"
								href={attachment.URL}
								target="_blank"
								rel="noopener noreferrer"
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									py: 0.5,
									px: 1,
									borderRadius: 1,
									textDecoration: "none",
									color: "text.primary",
									cursor: "pointer",
									transition: "background-color 0.15s",
									overflow: "hidden",
									flex: 1,
									minWidth: 0,
									"&:hover": {
										backgroundColor: "action.hover",
									},
								}}
							>
								<InsertDriveFileIcon
									fontSize="small"
									color="primary"
									sx={{ flexShrink: 0 }}
								/>
								<Typography
									variant="body2"
									sx={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{attachment.AttachmentName}
								</Typography>
							</Box>
							<ListItemSecondaryAction>
								<Tooltip title="Download">
									<IconButton
										size="small"
										component="a"
										href={attachment.URL}
										target="_blank"
										rel="noopener noreferrer"
										download={attachment.AttachmentName}
									>
										<GetAppIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Tooltip title="Remove">
									<IconButton
										size="small"
										onClick={() => handleDeleteClick(attachment)}
										disabled={disabled || isProcessing}
										color="error"
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</ListItemSecondaryAction>
						</ListItem>
					))}
				</List>
			)}

			{/* Delete confirmation */}
			<DeleteConfirmModal
				open={deleteModalOpen}
				title="REMOVE FILE"
				itemName={attachmentToDelete?.AttachmentName ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={() => {
					setDeleteModalOpen(false);
					setAttachmentToDelete(null);
				}}
			/>

			{/* Notifications */}
			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
